import { Controller } from "@hotwired/stimulus"

const COLORS = { serving: "#2563eb", dispatched: "#6366f1", available: "#059669", unavailable: "#6b7280" }
const DARK_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#1f2937" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#111827" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0f172a" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#374151" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] }
]

export default class extends Controller {
  static targets = ["canvas", "item", "filter", "search"]
  static values = { markers: Array, labels: Object, refreshSeconds: Number }

  connect() {
    this.status = "all"
    this.onMapsLoaded = () => this.render()
    document.addEventListener("google-maps-loaded", this.onMapsLoaded)
    if (window.google?.maps) this.render()
    this.timer = setInterval(() => this.refresh(), this.refreshSecondsValue * 1000)
  }

  disconnect() {
    document.removeEventListener("google-maps-loaded", this.onMapsLoaded)
    clearInterval(this.timer)
  }

  markersValueChanged() {
    if (this.map) this.drawMarkers()
  }

  refresh() {
    if (document.visibilityState === "visible") window.Turbo?.visit(window.location.href, { action: "replace" })
  }

  choose(event) {
    this.status = event.currentTarget.dataset.status
    this.filterTargets.forEach((button) => button.setAttribute("aria-pressed", String(button === event.currentTarget)))
    this.filter()
  }

  filter() {
    const term = this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : ""
    this.visible = new Set()
    this.itemTargets.forEach((item) => {
      const shown = (this.status === "all" || item.dataset.status === this.status) && item.dataset.search.includes(term)
      item.hidden = !shown
      if (shown) this.visible.add(Number(item.querySelector("[data-resource-id]").dataset.resourceId))
    })
    ;(this.canvasTarget.cocoMarkers || []).forEach((marker) => marker.setVisible(this.visible.has(marker.cocoData.id)))
  }

  focus(event) {
    const id = Number(event.currentTarget.dataset.resourceId)
    const marker = (this.canvasTarget.cocoMarkers || []).find((item) => item.cocoData.id === id)
    if (!marker) return

    this.map.panTo(marker.getPosition())
    this.openBalloon(marker)
  }

  render() {
    if (!window.google?.maps || !this.hasCanvasTarget) return
    if (!this.canvasTarget.cocoMap) {
      this.canvasTarget.cocoMap = new google.maps.Map(this.canvasTarget, {
        zoom: 11, center: { lat: -32.05, lng: -52.1 }, styles: DARK_STYLE, mapTypeControl: false, streetViewControl: false
      })
      this.canvasTarget.cocoInfo = new google.maps.InfoWindow()
    }
    this.map = this.canvasTarget.cocoMap
    this.info = this.canvasTarget.cocoInfo
    this.drawMarkers()
  }

  drawMarkers() {
    (this.canvasTarget.cocoMarkers || []).forEach((marker) => marker.setMap(null))
    const bounds = new google.maps.LatLngBounds()
    this.canvasTarget.cocoMarkers = this.markersValue.map((data) => {
      const position = { lat: data.lat, lng: data.lng }
      const marker = new google.maps.Marker({
        position,
        map: this.map,
        title: data.label,
        label: { text: data.label, color: "#ffffff", fontSize: "11px", fontWeight: "bold" },
        icon: { path: google.maps.SymbolPath.CIRCLE, scale: 14, fillColor: COLORS[data.status], fillOpacity: 0.95, strokeColor: "#ffffff", strokeWeight: 2 }
      })
      marker.cocoData = data
      marker.addListener("click", () => this.openBalloon(marker))
      bounds.extend(position)
      return marker
    })
    if (this.markersValue.length > 1) this.map.fitBounds(bounds)
    else if (this.markersValue.length === 1) this.map.setCenter(bounds.getCenter())
    this.filter()
  }

  openBalloon(marker) {
    this.info.setContent(this.balloon(marker.cocoData))
    this.info.open({ map: this.map, anchor: marker })
  }

  balloon(data) {
    const labels = this.labelsValue
    const element = document.createElement("div")
    element.style.cssText = "min-width:220px;font-size:12px;color:var(--mono-text);background:var(--mono-bg);padding:4px"

    const title = document.createElement("p")
    title.style.cssText = "font-weight:700;font-size:13px;margin-bottom:4px"
    title.textContent = `${data.label} · ${labels[data.status] || ""}`
    element.append(title)

    const lines = [
      [data.order, data.company].filter(Boolean).join(" · "),
      data.stage,
      data.reason,
      [
        data.knots !== null && data.knots !== undefined ? `${labels.speed}: ${data.knots} kn` : null,
        data.fuel !== null && data.fuel !== undefined ? `${labels.fuel}: ${Math.round(data.fuel)}%` : null
      ].filter(Boolean).join(" · "),
      data.consumed !== null && data.consumed !== undefined ? `${labels.consumed}: ${data.consumed} L` : null,
      data.positioned_at ? `${labels.positioned_at}: ${data.positioned_at}` : null
    ]
    lines.filter(Boolean).forEach((text) => {
      const line = document.createElement("p")
      line.style.color = "var(--mono-text-secondary)"
      line.textContent = text
      element.append(line)
    })

    if (data.order_url) {
      const actions = document.createElement("p")
      actions.style.cssText = "margin-top:6px;display:flex;gap:12px;font-weight:600"
      ;[[labels.open_order, data.order_url], [labels.open_chat, data.chat_url]].forEach(([text, href]) => {
        const link = document.createElement("a")
        link.href = href
        link.textContent = text
        link.dataset.turboFrame = "content-turbo-frame"
        link.style.color = "#60a5fa"
        actions.append(link)
      })
      element.append(actions)
    }
    return element
  }
}
